import {extend} from 'vee-validate';
import {
    email,
    image,
    integer,
    is,
    max,
    max_value,
    min,
    min_value,
    oneOf,
    required,
    ext,
    numeric,
} from 'vee-validate/dist/rules'

import dayjs from 'dayjs'
import lodash_map from "lodash/map";

extend('ext', {
    ...ext,
    message: (a, b) => {
        let numericKeys = Object.keys(b).filter((k) => !isNaN(k))
        let acceptedTypes = lodash_map(numericKeys, (key) => {
            return b[key]
        }).join(', ')
        return `Die Datei entspricht nicht den erlaubten Dateiformaten: ${acceptedTypes}.`
    }
});

extend('required', {
    ...required,
    message: 'Dieses Feld ist ein Pflichtfeld'
});

extend('integer', {
    ...integer,
    message: 'Muss eine Zahl sein'
});

extend('numeric', {
    ...numeric,
    message: 'Es sind nur Zahlen erlaubt'
});

extend('min_value', {
    ...min_value,
    message: 'Muss größer als {min} sein'
});

extend('min_money', {
    ...min_value,
    message: (fieldName, placeholders) => {
        return 'Mindestens ' + (new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(placeholders.min / 100))
    }
});

extend('max_money', {
    ...max_value,
    message: (fieldName, placeholders) => {
        return 'Höchstens ' + (new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(placeholders.max / 100))
    }
});

extend('max_value', {
    ...max_value,
    message: 'Muss kleiner als {max} sein'
});

extend('email', {
    ...email,
    message: 'Ungültige E-Mail-Adresse'
});

extend('min', {
    ...min,
    message: 'Mindestens {length} Zeichen'
});

extend('max', {
    ...max,
    message: 'Maximal {length} Zeichen'
});

extend('oneOf', {
    ...oneOf,
    message: 'Ungültige Auswahl'
});

extend('is', {
    ...is,
    message: 'Ungültige Eingabe'
});

extend('confirm', {
    params: ['target'],
    validate(value, {target}) {
        return value === target;
    },
    message: 'Passwörter stimmen nicht überein'
});

extend('image', {
    ...image,
    message: 'Datei ist kein Bild'
});

/**
 * Decode a file and resolve its natural size, or false when it is not a
 * decodable image. Deliberately no filename extension whitelist: that is what
 * used to make these rules reject webp on disks that accept it. Mime is the
 * built-in `image` rule's job, which every image input applies anyway.
 */
const naturalSize = (file) => {
    const URL = window.URL || window.webkitURL;

    return new Promise(resolve => {
        const image = new Image();
        image.onerror = () => resolve(false);
        image.onload = () => resolve({width: image.width, height: image.height});
        image.src = URL.createObjectURL(file);
    });
};

/**
 * Run a geometry check over every file, treating an undecodable one as valid:
 * it has no geometry to judge, and reporting it as e.g. "zu groß" would be
 * nonsense. The `decodable` rule below is what rejects it, with the right message.
 */
const everyImage = (files, check) => {
    files = Array.isArray(files) ? files : [files];

    return Promise.all(files.map(file => naturalSize(file).then(size => size === false || check(size))))
        .then(values => values.every(v => v));
};

// The picker's accept attribute is only a filter, and ImageInput's drag & drop
// never consults it — so the disks that cannot take webp (they feed notification
// mails, and Outlook cannot render it) need the check here too. Without this a
// dropped webp reaches su-api and comes back as "The file name does not match one
// of the allowed mime type." once the admin has already hit save.
const mimeParams = (params) => Object.keys(params).filter(k => !isNaN(k)).map(k => params[k]);

extend('accepted_types', {
    validate(files, params) {
        const allowed = mimeParams(params);
        files = Array.isArray(files) ? files : [files];

        return files.every(file => allowed.includes(file.type));
    },
    message: (field, params) => {
        const names = mimeParams(params).map(type => type.replace('image/', '').toUpperCase());

        return `Nur folgende Formate sind erlaubt: ${names.join(', ')}.`;
    }
});

// The built-in `image` rule only looks at the mime type, which browsers guess
// from the extension — a text file called photo.jpg sails through it.
extend('decodable', {
    validate(files) {
        files = Array.isArray(files) ? files : [files];

        return Promise.all(files.map(file => naturalSize(file).then(size => size !== false)))
            .then(values => values.every(v => v));
    },
    message: 'Das Bild konnte nicht gelesen werden. Bitte eine gültige JPEG-, PNG- oder WebP-Datei verwenden.'
});

// Integer comparison without tolerance: su-api runs the same check server-side
// (ImageSpec::matchesRatio) and a rounded one would let the two disagree.
extend('exact_ratio', {
    params: ['width', 'height'],
    validate(files, {width, height}) {
        width = parseInt(width);
        height = parseInt(height);

        return everyImage(files, (size) => size.width * height === size.height * width);
    },
    message: 'Das Bild muss ein Seitenverhältnis von {width}:{height} haben.'
});

// The target is the exact resolution the frontend renders at, so anything
// smaller would only be upscaled mush.
extend('min_dimensions', {
    params: ['width', 'height'],
    validate(files, {width, height}) {
        width = parseInt(width);
        height = parseInt(height);

        return everyImage(files, (size) => size.width >= width && size.height >= height);
    },
    message: 'Das Bild muss mindestens {width}×{height} Pixel groß sein.'
});

extend('max_pixels', {
    params: ['pixels'],
    validate(files, {pixels}) {
        pixels = parseInt(pixels);

        return everyImage(files, (size) => size.width * size.height <= pixels);
    },
    message: 'Die Auflösung des Bildes ist zu groß (max. 50 Megapixel).'
});

extend('size', {
    params: ['size'],
    validate(value, {size}) {
        if (Array.isArray(value)) {
            let isValid = true
            for (const file of value) {
                isValid = isValid && (file.size / 1024) < size
            }
            return isValid
        } else if (value instanceof File) {
            return (value.size / 1024) < size
        }
        return false
    },
    message: 'Ungültige Dateigröße'
});

extend('timeAfter', {
    params: ['target'],
    validate(value, {target}) {
        if (!value || !target) {
            return true
        }
        return value > target;
    },
    message: 'Endzeit muss nach Startzeit liegen'
});

extend('timeBefore', {
    params: ['target'],
    validate(value, {target}) {
        if (!value || !target) {
            return true
        }
        return value < target;
    },
    message: 'Startzeit muss vor Endzeit liegen'
});

extend('dateAfter', {
    params: ['target'],
    validate(value, {target}) {
        if (!value || !target) {
            return true
        }
        return dayjs(value, 'DD.MM.YYYY').isAfter(dayjs(target, 'DD.MM.YYYY'));
    },
    message: 'Endzeit muss nach Startzeit liegen'
});

extend('dateBefore', {
    params: ['target'],
    validate(value, {target}) {
        if (!value || !target) {
            return true
        }
        return dayjs(value, 'DD.MM.YYYY').isBefore(dayjs(target, 'DD.MM.YYYY'));
    },
    message: 'Startzeit muss vor Endzeit liegen'
});

extend('maxTime', {
    params: ['max'],
    validate(value, {max}) {
        if (!value || !max) {
            return true
        }
        let minutes = value.length > 2 ? value.slice(0, -2) : 0
        let seconds = value.length > 0 ? value.slice(-2) : 0
        let totalSeconds = parseInt(minutes) * 60 + parseInt(seconds)
        return totalSeconds < max
    },
    message: 'Ungültige Zeiteingabe'
});

extend('cme_points', {
    validate(value) {
        return /^[1-9]?[0-9](,[05])?$/.test(value)
    },
    message: 'Ungültige CME-Punkte'
});

extend('hex', {
    validate(value) {
        return /^#([\dA-F]{3}|[\dA-F]{6})$/.test(value);
    },
    message: 'Ungültige Farbe'
});

extend('url', {
    validate(value) {
        let pattern = new RegExp(/^(aaa|aaas|about|acap|acct|acd|acr|adiumxtra|adt|afp|afs|aim|amss|android|appdata|apt|ark|attachment|aw|barion|beshare|bitcoin|bitcoincash|blob|bolo|browserext|calculator|callto|cap|cast|casts|chrome|chrome-extension|cid|coap|coap\+tcp|coap\+ws|coaps|coaps\+tcp|coaps\+ws|com-eventbrite-attendee|content|conti|crid|cvs|dab|data|dav|diaspora|dict|did|dis|dlna-playcontainer|dlna-playsingle|dns|dntp|dpp|drm|drop|dtn|dvb|ed2k|elsi|example|facetime|fax|feed|feedready|file|filesystem|finger|first-run-pen-experience|fish|fm|ftp|fuchsia-pkg|geo|gg|git|gizmoproject|go|gopher|graph|gtalk|h323|ham|hcap|hcp|http|https|hxxp|hxxps|hydrazone|iax|icap|icon|im|imap|info|iotdisco|ipn|ipp|ipps|irc|irc6|ircs|iris|iris\.beep|iris\.lwz|iris\.xpc|iris\.xpcs|isostore|itms|jabber|jar|jms|keyparc|lastfm|ldap|ldaps|leaptofrogans|lorawan|lvlt|magnet|mailserver|mailto|maps|market|message|mid|mms|modem|mongodb|moz|ms-access|ms-browser-extension|ms-calculator|ms-drive-to|ms-enrollment|ms-excel|ms-eyecontrolspeech|ms-gamebarservices|ms-gamingoverlay|ms-getoffice|ms-help|ms-infopath|ms-inputapp|ms-lockscreencomponent-config|ms-media-stream-id|ms-mixedrealitycapture|ms-mobileplans|ms-officeapp|ms-people|ms-project|ms-powerpoint|ms-publisher|ms-restoretabcompanion|ms-screenclip|ms-screensketch|ms-search|ms-search-repair|ms-secondary-screen-controller|ms-secondary-screen-setup|ms-settings|ms-settings-airplanemode|ms-settings-bluetooth|ms-settings-camera|ms-settings-cellular|ms-settings-cloudstorage|ms-settings-connectabledevices|ms-settings-displays-topology|ms-settings-emailandaccounts|ms-settings-language|ms-settings-location|ms-settings-lock|ms-settings-nfctransactions|ms-settings-notifications|ms-settings-power|ms-settings-privacy|ms-settings-proximity|ms-settings-screenrotation|ms-settings-wifi|ms-settings-workplace|ms-spd|ms-sttoverlay|ms-transit-to|ms-useractivityset|ms-virtualtouchpad|ms-visio|ms-walk-to|ms-whiteboard|ms-whiteboard-cmd|ms-word|msnim|msrp|msrps|mss|mtqp|mumble|mupdate|mvn|news|nfs|ni|nih|nntp|notes|ocf|oid|onenote|onenote-cmd|opaquelocktoken|openpgp4fpr|pack|palm|paparazzi|payto|pkcs11|platform|pop|pres|prospero|proxy|pwid|psyc|pttp|qb|query|redis|rediss|reload|res|resource|rmi|rsync|rtmfp|rtmp|rtsp|rtsps|rtspu|s3|secondlife|service|session|sftp|sgn|shttp|sieve|simpleledger|sip|sips|skype|smb|sms|smtp|snews|snmp|soap\.beep|soap\.beeps|soldat|spiffe|spotify|ssh|steam|stun|stuns|submit|svn|tag|teamspeak|tel|teliaeid|telnet|tftp|tg|things|thismessage|tip|tn3270|tool|ts3server|turn|turns|tv|udp|unreal|urn|ut2004|v-event|vemmi|ventrilo|videotex|vnc|view-source|wais|webcal|wpid|ws|wss|wtai|wyciwyg|xcon|xcon-userid|xfire|xmlrpc\.beep|xmlrpc\.beeps|xmpp|xri|ymsgr|z39\.50|z39\.50r|z39\.50s):\/\/(((?:[_.\p{L}\p{N}-]|%[0-9A-Fa-f]{2})+:)?((?:[_.\p{L}\p{N}-]|%[0-9A-Fa-f]{2})+)@)?(([\p{L}\p{N}\p{S}\-_.])+(\.?([\p{L}\p{N}]|xn--[\p{L}\p{N}-]+)+\.?)|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|\[(?:(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){6})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:::(?:(?:(?:[0-9a-f]{1,4})):){5})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){4})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,1}(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){3})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,2}(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){2})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,3}(?:(?:[0-9a-f]{1,4})))?::(?:(?:[0-9a-f]{1,4})):)(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,4}(?:(?:[0-9a-f]{1,4})))?::)(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,5}(?:(?:[0-9a-f]{1,4})))?::)(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,6}(?:(?:[0-9a-f]{1,4})))?::))))\])(:[0-9]+)?(?:\/(?:[\p{L}\p{N}\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*(?:\?(?:[\p{L}\p{N}\-._~!$&'[\]()*+,;=:@/?]|%[0-9A-Fa-f]{2})*)?(?:#(?:[\p{L}\p{N}\-._~!$&'()*+,;=:@/?]|%[0-9A-Fa-f]{2})*)?$/iu);
        return pattern.test(value);
    },
    message: 'Ungültige URL'
});

const slugPattern = new RegExp(/^[a-z0-9-]+$/)

extend('slug', {
    validate(value) {
        return slugPattern.test(value);
    },
    message: 'Ungültiger Slug. Erlaubte Zeichen: Kleinbuchstaben, Zahlen, Bindestrich'
});

const filePattern = new RegExp(/^[A-Za-z0-9- _öäüÖÄÜß]+$/)

extend('filename', {
    validate(value) {
        return filePattern.test(value);
    },
    message: 'Ungültiger Slug. Erlaubte Zeichen: Buchstaben, Zahlen, Bindestrich, Leertaste'
});

function checkLuhn(value) {
    let sum = 0;
    let numDigits = value.length - 1;
    let parity = numDigits % 2;
    for (let i = numDigits; i >= 0; i--) {
        let digit = parseInt(value.substr(i, 1), 10);
        if (parity !== (i % 2)) {
            digit = digit << 1;
        }
        digit = (digit > 9) ? (digit - 9) : digit;
        sum += digit;
    }
    return (0 === (sum % 10))
}

extend('efn', {
    validate(efn) {
        if (!efn) {
            return true
        }
        for (let i = 0; i < 15; i++) {
            if (!/^\d+$/.test(efn)) {
                return false
            }
        }
        let countries = [
            '276',
            '040',
            '756'];
        if (countries.indexOf(efn.substr(2, 3)) === -1) {
            return false
        }
        return checkLuhn(efn)
    },
    message: 'Ungültige EFN'
});

extend('vnr', {
    validate(vnr) {
        if (!vnr) {
            return true
        }
        for (let i = 0; i < 19; i++) {
            if (!/^\d+$/.test(vnr)) {
                return false
            }
        }
        let countries = [
            '276'];
        if (countries.indexOf(vnr.substr(0, 3)) === -1) {
            return false
        }
        return checkLuhn(vnr)
    },
    message: 'Ungültige VNR'
});

extend('max_rich_text_length', {
    params: ['max'],
    validate(value, {max}) {
        if (!value || !max) {
            return true
        }
        return new DOMParser().parseFromString(value ? value : '', 'text/html').body.textContent.length <= max
    },
    message: 'Maximal {max} Zeichen',
});

extend('required_rich_text', {
    validate(value) {
        return new DOMParser().parseFromString(value ? value : '', 'text/html').body.textContent.trim().length > 0
    },
    message: 'Dieses Feld ist ein Pflichtfeld',
});

